import { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from '@/lib/toast';

const MAX_FEEDBACK_LEN = 4000;

const RATING_LABELS: Record<number, string> = {
  1: 'Hated it',
  2: 'Meh',
  3: 'OK',
  4: 'Good',
  5: 'Loved it',
};

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialText?: string;
}

export function FeedbackModal({ open, onOpenChange, initialText = '' }: FeedbackModalProps) {
  const [text, setText] = useState(initialText);
  const [rating, setRating] = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setText(initialText);
      setRating(null);
      setHoverRating(null);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [open, initialText]);

  const canSubmit = text.trim().length > 0 && text.length <= MAX_FEEDBACK_LEN && !submitting;

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await apiClient.submitFeedback(trimmed, rating ?? undefined);
      toast.success('Thanks, feedback sent! 🙏');
      onOpenChange(false);
      setText('');
      setRating(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSubmit) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const displayRating = hoverRating ?? rating ?? 0;
  const ratingLabel = displayRating > 0 ? RATING_LABELS[displayRating] : 'Rate your experience';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send feedback 💬</DialogTitle>
          <DialogDescription>
            Tell us what's working, what's broken, or what you'd like to see. The game session stays
            paused underneath — we'll keep everything right where you left it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <div
            className="flex items-center gap-1"
            onMouseLeave={() => setHoverRating(null)}
            role="radiogroup"
            aria-label="Rating"
          >
            {[1, 2, 3, 4, 5].map((star) => {
              const filled = star <= displayRating;
              return (
                <button
                  key={star}
                  type="button"
                  role="radio"
                  aria-checked={rating === star}
                  aria-label={`${star} ${star === 1 ? 'star' : 'stars'} — ${RATING_LABELS[star]}`}
                  onMouseEnter={() => setHoverRating(star)}
                  onClick={() => setRating(star)}
                  disabled={submitting}
                  className={cn(
                    'rounded p-1 transition-colors hover:bg-accent disabled:opacity-50',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                >
                  <Star
                    className={cn(
                      'h-6 w-6 transition-colors',
                      filled
                        ? 'fill-yellow-400 text-yellow-500 drop-shadow-[0_0_2px_rgba(250,204,21,0.5)]'
                        : 'fill-transparent text-muted-foreground'
                    )}
                  />
                </button>
              );
            })}
          </div>
          <p className="h-4 text-xs text-muted-foreground">{ratingLabel}</p>
        </div>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What's on your mind?"
          maxLength={MAX_FEEDBACK_LEN}
          rows={6}
          className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={submitting}
        />

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>⌘/Ctrl + Enter to send</span>
          <span>
            {text.length} / {MAX_FEEDBACK_LEN}
          </span>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Sending…' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
